import { Button, HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatPageTitle } from '@/constants/stringVars';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import queryKeys from '@/constants/queryKeys';
import { contentCreatorRoutes } from '@/constants/routes/app';
import useContentImageUpload from '@/hooks/content/useContentImageUpload';
import useToast from '@/hooks/useToast';
import {
  buildContentDetails,
  collectImageUploads,
  mapContentToFormValues,
} from '@/modules/content/creation/model/contentPayload';
import { TContentFormMode, TContentFormSubmit, WizardFinalAction } from '@/modules/content/creation/model/types';
import ContentWizard from '@/modules/content/creation/ContentWizard';
import svgIcons from '@/assets/svg/icons';
import { TCustomAxiosError } from '@/services/api';
import {
  createMuxUploadUrl,
  deleteContent,
  getContentById,
  publishContent,
  setYoutubeVideoSource,
  updateContent,
} from '@/services/api/requests/content';
import { uploadFileToMux } from '@/services/muxUpload';
import { useAppStore } from '@/store/useAppStore';
import { extra, text, text as textColors } from '@/theme/colors/palettes';
import roles from '@/constants/roles';
import { parseYouTubeId } from '@/modules/content/creation/model/youtube';
import LogoSpinner from '@/components/atoms/LogoSpinner';

const CreatorContentEdit = () => {
  const { contentId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user, organization } = useAppStore();

  const {
    data: content,
    isLoading,
    isFetchedAfterMount,
    isError,
  } = useQuery({
    queryKey: [queryKeys.content.byId, contentId],
    queryFn: () => getContentById(contentId),
    refetchOnMount: 'always',
  });

  const { uploadThumbnailAsync, uploadArticleImageAsync } = useContentImageUpload();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const savedThumbnailFile = useRef<File | null>(null);
  const savedVideoFile = useRef<File | null>(null);
  const savedYoutubeUrl = useRef('');
  const savedVideoSource = useRef<'mux' | 'youtube' | null>(null);

  const authorLabel = useMemo(() => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.name || 'You';
    return organization?.name ? `${name} | ${organization.name}` : name;
  }, [user, organization]);

  const orgId = user?.app_metadata?.primaryOrganizationId;
  const userRoles = user?.roles ?? [];
  const allowOrganizationVisibility = !(userRoles.includes(roles.contentCreator) && userRoles.length === 1);
  const contentFormDefaults = useMemo(() => {
    if (!content) return undefined;

    const defaults = mapContentToFormValues(content);

    return {
      ...defaults,
      isPublic: !allowOrganizationVisibility || defaults.isPublic,
    };
  }, [allowOrganizationVisibility, content]);

  useEffect(() => {
    if (content?.title) document.title = formatPageTitle(content.title);
  }, [content?.title]);

  const goToList = () => navigate({ to: contentCreatorRoutes.creatorLibrary });

  const handleDeleteDraft = async () => {
    if (!content || content.status !== 'draft' || isDeletingDraft) return;
    setIsDeletingDraft(true);
    try {
      await deleteContent(content.id);
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.mine] });
      queryClient.removeQueries({ queryKey: [queryKeys.content.byId, content.id] });
      setIsDeleteConfirmationOpen(false);
      toast({ status: 'success', title: 'Draft deleted.' });
      goToList();
    } catch (error) {
      toast({
        status: 'error',
        title: 'Could not delete draft',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message || 'Please try again.',
      });
    } finally {
      setIsDeletingDraft(false);
    }
  };

  const handleSubmit = async (
    mode: TContentFormMode,
    { values, thumbnailFile, removeThumbnail }: TContentFormSubmit,
  ) => {
    if (!content) return false;
    const id = content.id;
    const type = content.type;
    setIsSaving(true);
    try {
      await updateContent(id, {
        title: values.title,
        description: values.description,
        ...(content.status === 'draft' && removeThumbnail ? { thumbnailUrl: null } : {}),
        domains: values.domains,
        subdomains: values.subdomains,
        visibility:
          !allowOrganizationVisibility || values.isPublic
            ? { public: true, orgIds: [] }
            : { public: false, orgIds: orgId ? [orgId] : [] },
        details: buildContentDetails(type, values),
      });

      if (removeThumbnail) savedThumbnailFile.current = null;
      if (thumbnailFile && thumbnailFile !== savedThumbnailFile.current) {
        await uploadThumbnailAsync({ id, file: thumbnailFile });
        savedThumbnailFile.current = thumbnailFile;
      }

      // Published video sources are immutable. For drafts, preserve the remote
      // source unless the creator explicitly selected a replacement.
      if (type === 'video' && content.status !== 'published') {
        const persistedSource = savedVideoSource.current ?? values.originalVideoSource;
        const persistedYoutubeUrl = savedYoutubeUrl.current || values.originalYoutubeUrl || '';
        const youtubeChanged =
          values.videoSource === 'youtube' &&
          (persistedSource !== 'youtube' || parseYouTubeId(values.youtubeUrl) !== parseYouTubeId(persistedYoutubeUrl));

        if (youtubeChanged && values.youtubeUrl.trim()) {
          await setYoutubeVideoSource(id, values.youtubeUrl.trim());
          savedYoutubeUrl.current = values.youtubeUrl.trim();
          savedVideoSource.current = 'youtube';
        } else if (
          values.videoSource === 'mux' &&
          values.videoFile &&
          (savedVideoSource.current !== 'mux' || savedVideoFile.current !== values.videoFile)
        ) {
          const { uploadUrl } = await createMuxUploadUrl(id);
          setUploadPercent(0);
          await uploadFileToMux(uploadUrl, values.videoFile, setUploadPercent);
          setUploadPercent(null);
          savedVideoFile.current = values.videoFile;
          savedVideoSource.current = 'mux';
        }
      } else {
        for (const { sectionId, subsectionId, file } of collectImageUploads(values)) {
          await uploadArticleImageAsync({ id, file, sectionId, subsectionId });
        }
      }

      if (mode === 'publish' && content.status !== 'published') {
        await publishContent(id);
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.mine] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, id] });
      toast({
        id: crypto.randomUUID(),
        status: 'success',
        title: mode === 'draft' && content.status === 'draft' ? 'Draft saved' : 'Success',
        description:
          mode === 'publish' && content.status !== 'published'
            ? 'Content published.'
            : content.status === 'draft'
              ? 'Your draft has been saved.'
              : 'Changes saved.',
      });
      if (mode === 'draft' && content.status === 'draft') return true;
      goToList();
      return true;
    } catch (error) {
      setUploadPercent(null);
      toast({
        status: 'error',
        title: 'Error saving content',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message || 'Something went wrong. Please try again.',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // A published item only saves changes; a draft can be published from the preview step.
  const isPublished = content?.status === 'published';
  const primaryAction: WizardFinalAction = isPublished
    ? { key: 'save', label: 'Save changes', mode: 'draft', backgroundColor: 'primary.500' }
    : { key: 'publish', label: 'Publish Content', mode: 'publish', backgroundColor: 'secondary.500' };

  return (
    <VStack width={MAX_CONTAINER_WIDTH} gap={'8px'} alignItems={'stretch'}>
      <ModalContainer
        isOpen={isDeleteConfirmationOpen}
        onClose={() => {
          if (!isDeletingDraft) setIsDeleteConfirmationOpen(false);
        }}
        width="min(440px, calc(100vw - 32px))"
        backgroundColor={extra.white}
        closeOnOverlayClick={!isDeletingDraft}
        closeButtonHidden
      >
        <VStack align="stretch" gap="24px" padding="28px">
          <VStack align="stretch" gap="8px">
            <Text as="h2" fontFamily="Lora" fontSize="24px" fontWeight={500} color="text.darkBlue">
              Delete Draft?
            </Text>
            <Text color="text.mediumBlue">
              “{content?.title.trim() || 'Untitled draft'}” will be deleted. This can’t be undone.
            </Text>
          </VStack>
          <HStack justify="flex-end" gap="12px">
            <Button
              variant="outline"
              color="text.mediumBlue"
              borderColor="background.separatorBlue"
              onClick={() => setIsDeleteConfirmationOpen(false)}
              isDisabled={isDeletingDraft}
            >
              Cancel
            </Button>
            <Button
              backgroundColor="extra.red"
              color="white"
              _hover={{ backgroundColor: 'extra.darkRed' }}
              onClick={handleDeleteDraft}
              isLoading={isDeletingDraft}
              loadingText="Deleting"
            >
              Delete Draft
            </Button>
          </HStack>
        </VStack>
      </ModalContainer>
      <ModalContainer
        isOpen={uploadPercent !== null}
        onClose={() => {}}
        width={'720px'}
        backgroundColor={extra.white}
        closeOnOverlayClick={false}
        closeButtonHidden
      >
        <VStack paddingX={'32px'} paddingY={'36px'} gap={'18px'} align={'stretch'}>
          <Text variant={'loraMediumTitle'} color={textColors.darkBlue} textAlign={'center'}>
            Uploading your reel
          </Text>
          <Text fontSize={'14px'} color={textColors.mediumBlue} textAlign={'center'}>
            Please keep this tab open until the upload finishes.
          </Text>
          <Progress
            value={uploadPercent ?? 0}
            size={'md'}
            borderRadius={'full'}
            colorScheme={'purple'}
            hasStripe
            isAnimated
          />
          <Text fontSize={'13px'} fontWeight={700} color={textColors.mediumBlue} textAlign={'center'}>
            {uploadPercent ?? 0}%
          </Text>
        </VStack>
      </ModalContainer>

      {isLoading || !isFetchedAfterMount ? (
        <HStack width={'100%'} justifyContent={'center'} paddingY={'60px'}>
          <LogoSpinner />
        </HStack>
      ) : isError || !content ? (
        <>
          <HStack>
            <CustomButton
              isTransparent
              iconBeforeLabelSvgPath={svgIcons.leftArrow}
              iconBeforeLabelSvgBoxHeight={12}
              iconBeforeLabelSvgColor={text.mediumBlue}
              labelColor={'text.mediumBlue'}
              labelHoverColor={'extra.black'}
              label={'Back to Your Content'}
              onClick={goToList}
            />
          </HStack>
          <Text color={'extra.red'} marginTop={'20px'}>
            Couldn’t load this content.
          </Text>
        </>
      ) : (
        <ContentWizard
          key={contentId}
          type={content.type}
          authorLabel={authorLabel}
          defaultValues={contentFormDefaults}
          defaultThumbnailUrl={content.effectiveThumbnailUrl ?? content.thumbnailUrl}
          canRemoveThumbnail={content.status === 'draft' && Boolean(content.thumbnailUrl)}
          allowOrganizationVisibility={allowOrganizationVisibility}
          isSubmitting={isSaving}
          onExit={goToList}
          onSubmit={handleSubmit}
          isEditingDraft={content.status === 'draft'}
          secondaryAction={{ key: 'save', label: 'Save changes', mode: 'draft', isTransparent: true }}
          onDeleteDraft={content.status === 'draft' ? () => setIsDeleteConfirmationOpen(true) : undefined}
          isDeletingDraft={isDeletingDraft}
          primaryAction={primaryAction}
          requireChangesToContinue={isPublished}
          isVideoSourceReadOnly={isPublished}
        />
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/_content/creator/content/$contentId')({
  staticData: { title: 'Content' },
  component: CreatorContentEdit,
});
